import { ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bell, LogOut, User } from 'lucide-react'
import PaletteMenu from './PaletteMenu'
import { GATE_HOME_URL } from '../utils/gate'
import { Theme } from '../hooks/useTheme'
import { useAuth } from '../contexts/AuthContext'
import { useYear } from '../contexts/YearContext'
import DebugInfo from './DebugInfo'
import FeedbackWidget from './FeedbackWidget'
import AppLink from './AppLink'
import toast from 'react-hot-toast'
import { api, AppNotification } from '../api/api'

interface LayoutProps {
  children: ReactNode
  theme: Theme
  onThemeToggle: () => void
}

export default function Layout({ children, theme, onThemeToggle }: LayoutProps) {
  const location = useLocation()
  // Таблицы-рабочие страницы (журнал СИ, передачи) занимают всю ширину экрана, остальные остаются в центральной колонке
  const wideLayout = location.pathname.startsWith('/journal') || location.pathname.startsWith('/lab-transfers') || location.pathname.startsWith('/sheets')
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { year, setYear, availableYears, division, setDivision } = useYear()
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [newUnreadCount, setNewUnreadCount] = useState(0)
  const [criticalCount, setCriticalCount] = useState(0)
  const [latestNotifications, setLatestNotifications] = useState<AppNotification[]>([])
  const notificationsMenuRef = useRef<HTMLDivElement | null>(null)

  const handleLogout = () => {
    logout()
    toast.success('Выход выполнен')
    navigate('/login')
  }


  const loadNotifications = async () => {
    if (!user) return
    try {
      const [countResp, listResp] = await Promise.all([api.getNotificationsUnreadCount(), api.listNotifications(false)])
      setUnreadCount(countResp.unread || 0)
      setNewUnreadCount(countResp.new_unread || 0)
      setCriticalCount(countResp.critical || 0)
      setLatestNotifications((listResp.notifications || []).slice(0, 5))
    } catch {
      // ignore silently
    }
  }

  useEffect(() => {
    void loadNotifications()
    const timer = window.setInterval(() => {
      void loadNotifications()
    }, 60000)
    return () => window.clearInterval(timer)
  }, [user])

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!notificationsMenuRef.current) return
      if (!notificationsMenuRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (location.pathname === '/login') {
    return <>{children}</>
  }

  return (
    <div className="min-h-screen min-w-0 max-w-full overflow-x-hidden bg-gray-50 dark:bg-gray-900">
      <header className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className={`flex flex-wrap items-center gap-2 px-3 py-2 sm:px-4 mx-auto max-w-7xl`}>
          <a
            href={GATE_HOME_URL}
            className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
            title="Вернуться на ecmit.su к списку сервисов"
          >
            <span aria-hidden="true">←</span> На ecmit.su
          </a>
          <AppLink to="/" className="shrink-0 text-lg font-bold text-gray-950 dark:text-white" aria-label="Главная страница">
            CRM
          </AppLink>

          <div className="order-3 flex w-full min-w-0 flex-wrap items-center gap-2 border-t border-gray-200 pt-2 dark:border-gray-700 lg:order-none lg:w-auto lg:flex-1 lg:border-0 lg:pt-0">
            {user && (
              <div className="flex shrink-0 items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {availableYears.map((availableYear) => (
                  <button
                    key={availableYear}
                    onClick={() => setYear(availableYear)}
                    className={`px-2 sm:px-3 py-1 text-sm font-medium rounded transition-colors ${
                      year === availableYear ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {availableYear}
                  </button>
                ))}
              </div>
            )}

            {user && (
              <div className="flex shrink-0 items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1" aria-label="Подразделение">
                <button
                  onClick={() => setDivision('lmo3')}
                  className={`px-2 sm:px-3 py-1 text-sm font-medium rounded transition-colors ${division === 'lmo3' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  ЛМО-3
                </button>
                <button
                  onClick={() => setDivision('spb')}
                  className={`px-2 sm:px-3 py-1 text-sm font-medium rounded transition-colors ${division === 'spb' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  СПб
                </button>
              </div>
            )}

            {user && location.pathname !== '/' && (
              <AppLink
                to="/"
                className="shrink-0 px-2 sm:px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                Главная
              </AppLink>
            )}

            {user && location.pathname !== '/docs' && (
              <AppLink
                to="/docs"
                className="shrink-0 px-2 sm:px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                Доки
              </AppLink>
            )}

            {user && location.pathname !== '/chat' && (
              <AppLink
                to="/chat"
                className="shrink-0 px-2 sm:px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                Чат
              </AppLink>
            )}
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            {user && <AppLink to="/profile" className="inline-flex h-9 max-w-36 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-2 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700" title="Личный кабинет"><User className="h-4 w-4" /><span className="hidden truncate text-sm lg:inline">{user.name}</span></AppLink>}

            {user && (
              <div className="relative" ref={notificationsMenuRef}>
                <button
                  onClick={() => setIsNotificationsOpen((prev) => !prev)}
                  className={`relative shrink-0 p-2 rounded-lg border transition-colors ${
                    criticalCount > 0
                      ? 'border-red-300 text-red-600 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                      : newUnreadCount > 0
                        ? 'border-blue-300 text-blue-600 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                        : 'border-gray-300 text-gray-600 bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                  title="Уведомления"
                >
                  <Bell size={18} />
                    {newUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] leading-[18px] text-center">
                        {newUnreadCount > 99 ? '99+' : newUnreadCount}
                      </span>
                    )}
                </button>
                {isNotificationsOpen && (
                  <div className="fixed left-3 right-3 mt-2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg z-50 dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:left-auto sm:right-0 sm:w-[360px]">
                    <div className="px-2 py-1 text-sm font-medium text-gray-900 dark:text-white">Уведомления</div>
                    <div className="max-h-80 overflow-auto">
                      {latestNotifications.length ? (
                        latestNotifications.map((n) => (
                          <AppLink
                            key={n.id}
                            to="/notifications"
                            onClick={() => {
                              if (!n.read_at) {
                                void api.markNotificationRead(n.id).catch(() => undefined)
                              }
                              setIsNotificationsOpen(false)
                              void loadNotifications()
                            }}
                            className="w-full text-left px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{n.title}</div>
                            <div className="break-words text-xs text-gray-600 dark:text-gray-300">{n.message}</div>
                          </AppLink>
                        ))
                      ) : (
                        <div className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">Новых уведомлений нет</div>
                      )}
                    </div>
                    <AppLink
                      to="/notifications"
                      onClick={() => {
                        setIsNotificationsOpen(false)
                      }}
                      className="mt-2 w-full btn btn-secondary"
                    >
                      Все уведомления
                    </AppLink>
                  </div>
                )}
              </div>
            )}

            <AppLink
              to="/changelog"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100 text-base hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
              title="История обновлений"
              aria-label="История обновлений"
            >
              ✨
            </AppLink>

            <PaletteMenu />

            <button
              onClick={onThemeToggle}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              title={theme === 'light' ? 'Тёмная тема' : 'Светлая тема'}
              aria-label={theme === 'light' ? 'Включить тёмную тему' : 'Включить светлую тему'}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>

            {user && (
              <button
                onClick={handleLogout}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-red-300 px-2 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                title="Выйти"
                aria-label="Выйти"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden xl:inline">Выйти</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className={wideLayout ? 'min-w-0 w-full px-2 py-3 sm:px-3' : 'mx-auto min-w-0 max-w-4xl px-3 py-4 sm:px-4 sm:py-6'}>{children}</main>

      <DebugInfo />
      {location.pathname === '/' && <FeedbackWidget />}
    </div>
  )
}
